// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Reorder } from 'framer-motion';
import { Community, HydratedPost, Post, UserData } from '../types';
import { fetchAllCommunities, fetchJoinedCommunitiesPosts, createCommunityOnServer, updateUserOnServer } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { UsersIcon, MailIcon, FilterIcon, HashtagIcon, CheckmarkIcon, PlusIcon, SearchIcon, UserIcon, EditIcon, CloseIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { PostGrid } from './PostGrid';

interface CommunitiesPageProps {
    onNavigateToCommunity: (id: string, channelId?: string) => void;
    currentUserId: string;
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number) => void;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onReportPost?: (postId: number) => void;
    onVouch?: (postId: number) => void;
    onOpenCreateYipYapWithUser?: (userId: string) => void;
    onOpenCreateCommunityWithUser?: (userId: string) => void;
    onOpenNewMessage?: () => void;
    onOpenCreateYipYap?: () => void;
    onOpenCreateCommunity?: () => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    getHydratedPosts: (posts: Post[]) => HydratedPost[];
    usersData: Record<string, UserData>;
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    onScaleChange?: (scale: number) => void;
    isDemo?: boolean;
    isShakingMode?: boolean;
    postcardGap?: number;
    onPostcardGapChange?: (gap: number) => void;
    isModalOpen?: boolean;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    initialVisibleCount?: number;
    onVisibleCountChange?: (count: number) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    isLoading?: boolean;
    posts: Post[];
    communities: Community[];
    setCommunities: React.Dispatch<React.SetStateAction<Community[]>>;
    initialCreateModalOpen?: boolean;
    initialCreateWithUser?: string | null;
    onCloseCreateModal?: () => void;
    onlyRenderCreateModal?: boolean;
}

const DragHandleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M7 10h10a1 1 0 0 0 0-2H7a1 1 0 0 0 0 2zm0 6h10a1 1 0 0 0 0-2H7a1 1 0 0 0 0 2z" />
    </svg>
);

export const CommunitiesPage = ({
    onNavigateToCommunity,
    currentUserId,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onNavigateToHashtag,
    onReportPost,
    onVouch,
    onOpenCreateYipYapWithUser,
    onOpenCreateCommunityWithUser,
    onOpenNewMessage,
    onOpenCreateYipYap,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    getHydratedPosts,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    onSwipeTransition,
    isDemo = false,
    isShakingMode = false,
    postcardGap = 8,
    onPostcardGapChange,
    isModalOpen = false,
    onShare,
    initialVisibleCount,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading: isGlobalLoading,
    posts,
    communities,
    setCommunities,
    initialCreateModalOpen = false,
    initialCreateWithUser = null,
    onCloseCreateModal,
    onlyRenderCreateModal = false
}: CommunitiesPageProps) => {
    const { updateUserData, setUsersData, usersData } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(initialCreateModalOpen);
    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const [selectedFilterCommunities, setSelectedFilterCommunities] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'feed' | 'explore'>('feed');

    const currentUserData = usersData[currentUserId];
    const themeColor = currentUserData?.appThemeColor || '#1b1b1f';
    const textColor = currentUserData?.appTextColor || '#ffffff';
    const opacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 80) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };
    const modalBg = getExactModalBg(themeColor, opacity);

    const [draggedCommunityId, setDraggedCommunityId] = useState<string | null>(null);
    const [dragOverCommunityId, setDragOverCommunityId] = useState<string | null>(null);

    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDraggedCommunityId(id);
        e.dataTransfer.effectAllowed = 'move';
        if (e.dataTransfer.setDragImage) {
            const el = document.createElement('div');
            el.style.opacity = '0';
            document.body.appendChild(el);
            e.dataTransfer.setDragImage(el, 0, 0);
            setTimeout(() => document.body.removeChild(el), 0);
        }
    };

    const handleDragOver = (e: React.DragEvent, id: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverCommunityId !== id) {
            setDragOverCommunityId(id);
        }
    };

    const handleDragEnd = () => {
        setDraggedCommunityId(null);
        setDragOverCommunityId(null);
    };

    const handleDrop = async (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedCommunityId || draggedCommunityId === targetId) {
            handleDragEnd();
            return;
        }

        const joined = communities.filter(c => c.members?.includes(currentUserId));
        const currentUserData = usersData[currentUserId];
        const order = currentUserData?.communitiesOrder || [];

        const sorted = [...joined].sort((a, b) => {
            const aIdx = order.indexOf(a._id);
            const bIdx = order.indexOf(b._id);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;

            const pinned = currentUserData?.pinnedCommunities || [];
            const aPinned = pinned.find((p: any) => p.communityId === a._id);
            const bPinned = pinned.find((p: any) => p.communityId === b._id);
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;
            if (aPinned && bPinned) return new Date(aPinned.pinnedAt).getTime() - new Date(bPinned.pinnedAt).getTime();
            return a.name.localeCompare(b.name);
        });

        const draggedIdx = sorted.findIndex(c => c._id === draggedCommunityId);
        const targetIdx = sorted.findIndex(c => c._id === targetId);

        if (draggedIdx !== -1 && targetIdx !== -1) {
            const [removed] = sorted.splice(draggedIdx, 1);
            sorted.splice(targetIdx, 0, removed);
            
            const newOrder = sorted.map(c => c._id);
            
            setUsersData((prev: Record<string, UserData>) => ({
                ...prev,
                [currentUserId]: {
                    ...prev[currentUserId],
                    communitiesOrder: newOrder
                }
            }));

            try {
                await updateUserOnServer(currentUserId, { communitiesOrder: newOrder });
            } catch (err) {
                console.error('Failed to update communities order', err);
            }
        }
        
        handleDragEnd();
    };

    const currentCommunityStmt = usersData['goodlyfe']?.communityStatement || 'Welcome to the communities! Join the conversation or start your own.';
    const [communityStmtInput, setCommunityStmtInput] = useState(currentCommunityStmt);

    useEffect(() => {
        setCommunityStmtInput(currentCommunityStmt);
    }, [currentCommunityStmt]);

    useEffect(() => {
        setShowCreateModal(initialCreateModalOpen);
    }, [initialCreateModalOpen]);

    useEffect(() => {
        onInternalModalStateChange?.(showCreateModal || sidebarOpen);
    }, [showCreateModal, sidebarOpen, onInternalModalStateChange]);

    useEffect(() => {
        if (closeInternalModalsTrigger > 0) {
            setShowCreateModal(false);
            setSidebarOpen(false);
        }
    }, [closeInternalModalsTrigger]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isDemo) {
            alert("Interactions are disabled in Play Around mode. Log in to create communities!");
            setShowCreateModal(false);
            return;
        }
        const form = e.target as HTMLFormElement;
        const name = (form.elements.namedItem('name') as HTMLInputElement).value;
        const bio = (form.elements.namedItem('bio') as HTMLInputElement).value;
        const isPrivate = (form.elements.namedItem('isPrivate') as HTMLInputElement).checked;

        try {
            const newCommunity = await createCommunityOnServer({ name, bio, creator: currentUserId, isPrivate, whoCanPost: (form.elements.namedItem('whoCanPost') as HTMLSelectElement).value });
            setCommunities(prev => [...prev, newCommunity]);
            
            if (initialCreateWithUser) {
                import('../api').then(({ joinCommunityOnServer }) => joinCommunityOnServer(newCommunity._id, initialCreateWithUser, newCommunity.inviteLink, currentUserId));
            }
            
            onNavigateToCommunity(newCommunity.name);
            setShowCreateModal(false);
            if (onCloseCreateModal) onCloseCreateModal();
        } catch (error: any) {
            const serverError = error.response?.data?.error;
            const details = error.response?.data?.details;
            alert(serverError ? `${serverError}${details ? `: ${details}` : ''}` : 'Failed to create community');
        }
    };

    const displayedCommunities = useMemo(() => {
        return communities.filter(c => {
            const isMember = c.members?.some((m: string) => m.toLowerCase() === currentUserId?.toLowerCase());
            if (activeTab === 'explore') return !isMember && !c.isPrivate;
            return false;
        });
    }, [communities, currentUserId, activeTab]);

    const toggleFilter = (communityId: string) => {
        setSelectedFilterCommunities(prev =>
            prev.includes(communityId)
                ? prev.filter(id => id !== communityId)
                : [...prev, communityId]
        );
    };

    const isMobile = window.innerWidth <= 768;

    const joinedCommunityIds = useMemo(() => {
        return communities.filter(c => c.members?.includes(currentUserId)).map(c => c._id);
    }, [communities, currentUserId]);

    const communityFeedPosts = useMemo(() => {
        const rawCommunityPosts = posts.filter(p => p.communityId && joinedCommunityIds.includes(p.communityId));
        return getHydratedPosts(rawCommunityPosts);
    }, [posts, joinedCommunityIds, getHydratedPosts]);

    const filteredFeedPosts = useMemo(() => {
        return communityFeedPosts.filter(post =>
            selectedFilterCommunities.length === 0 ||
            (post.communityId && selectedFilterCommunities.includes(post.communityId))
        );
    }, [communityFeedPosts, selectedFilterCommunities]);

    if (onlyRenderCreateModal) {
        return showCreateModal ? (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setShowCreateModal(false); if (onCloseCreateModal) onCloseCreateModal(); }}>
                <div 
                    className="p-6 md:p-8 rounded-[2.5rem] w-full max-w-md shadow-2xl relative" 
                    style={{ backgroundColor: modalBg, color: textColor }}
                    onClick={e => e.stopPropagation()}
                >
                    <button
                        onClick={() => { setShowCreateModal(false); if (onCloseCreateModal) onCloseCreateModal(); }}
                        className="absolute top-6 right-6 text-gray-500 hover:text-white"
                    >
                        ✕
                    </button>
                    <form onSubmit={handleCreate}>
                        <div className="pb-8 border-b border-white/5 bg-gradient-to-br from-blue-600/10 to-transparent">
                            <h2 className="text-2xl font-bold text-raised mb-1" style={{ color: themeColor, ...stickerTextStyle(0.15) }}>Create Community</h2>
                            <p className="text-sm text-gray-400">Start a new space for your interests and connections.</p>
                        </div>
                        <div className="py-8 space-y-6">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Community Name</label>
                                <input name="name" type="text" required placeholder="e.g. Photography Lovers" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Description</label>
                                <textarea name="bio" placeholder="Tell everyone what this group is about..." className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 transition-colors h-32 resize-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Who can post?</label>
                                    <select name="whoCanPost" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 appearance-none">
                                        <option value="all">Everyone</option>
                                        <option value="admins">Only Admins</option>
                                    </select>
                                </div>
                                <div className="flex items-end mb-4">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative">
                                            <input name="isPrivate" type="checkbox" className="peer sr-only" />
                                            <div className="h-6 w-11 rounded-full bg-white/10 transition-colors peer-checked:bg-blue-600"></div>
                                            <div className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-all peer-checked:left-6"></div>
                                        </div>
                                        <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">Private Community</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="p-8 bg-black/20 flex gap-4">
                            <button type="button" onClick={() => { setShowCreateModal(false); if (onCloseCreateModal) onCloseCreateModal(); }} className="flex-1 py-4 text-gray-400 font-bold hover:text-white transition-colors">Cancel</button>
                            <button type="submit" className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20">Create Community</button>
                        </div>
                    </form>
                </div>
            </div>
        ) : null;
    }

    return (
        <div className="flex w-full">
            {/* Communities Modal Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[150] animate-fade-in"
                    onClick={() => setSidebarOpen(false)}
                >
                    <div
                        className="rounded-3xl shadow-2xl flex flex-col w-full max-w-sm mx-4 overflow-hidden border border-white/10 backdrop-blur-md"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'var(--app-theme-bg)',
                            color: 'var(--app-text-color)'
                        }}
                    >
                        {/* Modal Header */}
                        <div className="flex-shrink-0 p-5 border-b border-white/10 bg-black/25 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <UsersIcon className="w-5 h-5 opacity-80" style={{ color: 'var(--app-text-color)' }} />
                                <div className="min-w-0">
                                    <h2 className="text-lg font-black uppercase italic tracking-tighter truncate" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.3) }}>
                                        Communities
                                    </h2>
                                    <p className="text-[10px] tracking-wider font-semibold uppercase opacity-60">Your Communities</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="p-2 hover:bg-white/10 active:scale-95 rounded-full transition-all opacity-70 hover:opacity-100"
                                style={{ color: 'var(--app-text-color)' }}
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content - Communities List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[50vh] no-scrollbar">
                            {(() => {
                                const joined = communities.filter(c => c.members?.includes(currentUserId));
                                const currentUserData = usersData[currentUserId];
                                const order = currentUserData?.communitiesOrder || [];

                                return joined.sort((a, b) => {
                                    const aIdx = order.indexOf(a._id);
                                    const bIdx = order.indexOf(b._id);
                                    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                                    if (aIdx !== -1) return -1;
                                    if (bIdx !== -1) return 1;

                                    const pinned = currentUserData?.pinnedCommunities || [];
                                    const aPinned = pinned.find((p: any) => p.communityId === a._id);
                                    const bPinned = pinned.find((p: any) => p.communityId === b._id);
                                    if (aPinned && !bPinned) return -1;
                                    if (!aPinned && bPinned) return 1;
                                    if (aPinned && bPinned) return new Date(aPinned.pinnedAt).getTime() - new Date(bPinned.pinnedAt).getTime();
                                    return a.name.localeCompare(b.name);
                                }).map(community => {
                                    const isSelected = selectedFilterCommunities.includes(community._id);
                                    return (
                                        <div 
                                            key={community._id} 
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, community._id)}
                                            onDragOver={(e) => handleDragOver(e, community._id)}
                                            onDrop={(e) => handleDrop(e, community._id)}
                                            onDragEnd={handleDragEnd}
                                            className={`group relative flex items-center gap-2 ${draggedCommunityId === community._id ? 'opacity-30' : 'opacity-100'} ${dragOverCommunityId === community._id ? 'border-t-2 border-blue-500' : ''} transition-all duration-200`}
                                        >
                                            <button
                                                onClick={() => { onNavigateToCommunity(community.name); setSidebarOpen(false); }}
                                                className="flex-grow flex items-center gap-3 p-2.5 rounded-xl transition-all hover:bg-white/5 text-left min-w-0"
                                                style={{ color: 'var(--app-text-color)' }}
                                            >
                                                <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden">
                                                    {community.avatar ? (
                                                        <img src={community.avatar} alt="" className="w-full h-full object-cover shadow-md" />
                                                    ) : (
                                                        <div className="w-full h-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-xs font-bold">
                                                            {community.name[0]}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium truncate opacity-90 group-hover:opacity-100 transition-opacity">
                                                    {community.name}
                                                </span>
                                            </button>

                                            <div className="opacity-0 group-hover:opacity-100 p-1 cursor-grab active:cursor-grabbing text-gray-500 hover:text-white transition-opacity flex-shrink-0" title="Drag to reorder">
                                                <DragHandleIcon className="w-4 h-4" />
                                            </div>

                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleFilter(community._id); }}
                                                className={`p-2 rounded-full transition-all duration-300 flex-shrink-0 ${isSelected ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-white/5 text-gray-500 hover:text-white hover:bg-white/10'}`}
                                                title={isSelected ? "Remove filter" : "Filter by this community"}
                                            >
                                                {isSelected ? <CheckmarkIcon className="w-3.5 h-3.5" /> : <FilterIcon className="w-3.5 h-3.5" />}
                                            </button>
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex-shrink-0 p-4 border-t border-white/10 bg-black/25 flex flex-col gap-3">
                            <button
                                onClick={() => { setSidebarOpen(false); setShowCreateModal(true); }}
                                className="w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm bg-white/10 hover:bg-white/15 active:scale-98 transition-all"
                                style={{ color: 'var(--app-text-color)' }}
                            >
                                <PlusIcon className="w-4 h-4" />
                                <span>Create Community</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-grow w-full pb-32 relative z-10">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex justify-end items-center mb-6 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] gap-6 relative px-2">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="text-3xl hover:scale-110 active:scale-95 transition-transform drop-shadow-md"
                            title="Your Communities"
                            style={{ color: 'var(--app-text-color)' }}
                        >
                            <UsersIcon className="w-9 h-9 sm:w-10 sm:h-10 drop-shadow-md rounded-full" />
                        </button>

                        <button
                            onClick={() => setActiveTab('feed')}
                            className={`text-[36px] sm:text-[40px] leading-none hover:scale-110 active:scale-95 transition-transform drop-shadow-md ${activeTab === 'feed' ? 'opacity-100 scale-110' : 'opacity-60 hover:opacity-100'}`}
                            title="Community Posts"
                        >
                            🌐
                        </button>

                        <button
                            onClick={() => setActiveTab('explore')}
                            className={`transition-transform hover:scale-110 active:scale-95 flex items-center ${activeTab === 'explore' ? 'text-blue-400 scale-110' : 'text-white opacity-80 hover:opacity-100'}`}
                            title="Find Communities"
                        >
                            <SearchIcon className="w-8 h-8 drop-shadow-md" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto mb-12 px-4 mt-8 w-full">
                        <div className="flex justify-center md:justify-end">
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="relative group cursor-pointer transition-transform hover:scale-105 active:scale-95 flex items-center justify-center"
                                style={{ width: 'min(90vw, 350px)', height: '120px' }}
                            >
                                {/* Zigzagged black background (SVG explosion) */}
                                <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[65%] h-[105%] text-black drop-shadow-xl pointer-events-none" viewBox="0 0 200 100" preserveAspectRatio="none" fill="currentColor">
                                    <path d="M 0 50 L 15 35 L 5 15 L 30 25 L 45 5 L 65 25 L 85 0 L 100 20 L 120 5 L 140 25 L 165 10 L 175 35 L 200 45 L 185 60 L 195 85 L 170 75 L 150 95 L 130 75 L 110 100 L 90 80 L 65 95 L 50 75 L 30 90 L 20 70 L 0 50 Z" />
                                </svg>

                                {/* Text container */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center select-none pointer-events-none"
                                    style={{ transform: 'perspective(500px) rotateX(20deg) rotateY(-15deg) rotateZ(-3deg) skewX(-5deg) translateZ(10px)' }}
                                >
                                    {/* Layer 1: Red outer block / thick stroke */}
                                    <div
                                        className="absolute uppercase whitespace-nowrap text-center"
                                        style={{
                                            fontFamily: "'Bangers', cursive",
                                            fontSize: 'min(11vw, 4rem)',
                                            lineHeight: '0.9',
                                            letterSpacing: '2px',
                                            color: '#E60000',
                                            WebkitTextStroke: '14px #E60000',
                                            zIndex: 10,
                                            textShadow: '4px 4px 0 #000, 6px 6px 0 #000'
                                        }}
                                    >
                                        START A<br />COMMUNITY!
                                    </div>

                                    {/* Layer 2: 3D Cel-Shaded Extrusion (Middle) */}
                                    <div
                                        className="absolute uppercase whitespace-nowrap text-center"
                                        style={{
                                            fontFamily: "'Bangers', cursive",
                                            fontSize: 'min(11vw, 4rem)',
                                            lineHeight: '0.9',
                                            letterSpacing: '2px',
                                            color: '#FF8A00',
                                            WebkitTextStroke: '1px rgba(0,0,0,0.15)',
                                            zIndex: 15,
                                            textShadow: '1px 1px 0px #000, 2px 2px 0px rgba(0,0,0,0.8), 3px 3px 0px #000, 4px 4px 0px rgba(0,0,0,0.8), 5px 5px 0px #000'
                                        }}
                                    >
                                        START A<br />COMMUNITY!
                                    </div>

                                    {/* Layer 3: Yellow/Orange Gradient Fill (Top) */}
                                    <div
                                        className="absolute uppercase whitespace-nowrap text-center"
                                        style={{
                                            fontFamily: "'Bangers', cursive",
                                            fontSize: 'min(11vw, 4rem)',
                                            lineHeight: '0.9',
                                            letterSpacing: '2px',
                                            background: 'linear-gradient(180deg, #FFF000 0%, #FF8A00 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            zIndex: 20
                                        }}
                                    >
                                        START A<br />COMMUNITY!
                                    </div>

                                    {/* Layer 4: Faint sketchy over-draw lines */}
                                    <div
                                        className="absolute uppercase whitespace-nowrap text-center"
                                        style={{
                                            fontFamily: "'Bangers', cursive",
                                            fontSize: 'min(11vw, 4rem)',
                                            lineHeight: '0.9',
                                            letterSpacing: '2px',
                                            color: 'transparent',
                                            WebkitTextStroke: '1px rgba(0,0,0,0.15)',
                                            transform: 'translate(1px, -1px) rotate(0.2deg)',
                                            zIndex: 30
                                        }}
                                    >
                                        START A<br />COMMUNITY!
                                    </div>
                                    <div
                                        className="absolute uppercase whitespace-nowrap text-center"
                                        style={{
                                            fontFamily: "'Bangers', cursive",
                                            fontSize: 'min(11vw, 4rem)',
                                            lineHeight: '0.9',
                                            letterSpacing: '2px',
                                            color: 'transparent',
                                            WebkitTextStroke: '1px rgba(0,0,0,0.1)',
                                            transform: 'translate(-1px, 1px) rotate(-0.2deg)',
                                            zIndex: 30
                                        }}
                                    >
                                        START A<br />COMMUNITY!
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="flex justify-center md:justify-start w-full">
                            {((currentUserId === 'goodlyfe' || currentUserId === 'agk' || usersData[currentUserId || '']?.role === 'admin')) ? (
                                <div className="flex flex-col gap-2 max-w-md w-full items-center">
                                    <textarea
                                        value={communityStmtInput}
                                        onChange={(e) => setCommunityStmtInput(e.target.value)}
                                        className="w-full p-3 bg-black/30 text-white rounded-xl border border-white/10 focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-xl font-bold leading-relaxed resize-y"
                                        placeholder="Edit community statement..."
                                        rows={3}
                                        style={stickerTextStyle(0.5)}
                                    />
                                    {communityStmtInput !== currentCommunityStmt && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    await updateUserOnServer('goodlyfe', { communityStatement: communityStmtInput });
                                                    if (typeof setUsersData === 'function') {
                                                        setUsersData((prev: any) => ({
                                                            ...prev,
                                                            goodlyfe: { ...prev.goodlyfe, communityStatement: communityStmtInput }
                                                        }));
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                } catch (e) {
                                                    console.error('Failed to update statement', e);
                                                    alert('Failed to update statement');
                                                }
                                            }}
                                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-full transition-colors"
                                        >
                                            Save Statement
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 max-w-md w-full justify-center">
                                    <p className="text-white text-xl font-bold leading-relaxed text-center" style={stickerTextStyle(0.5)}>
                                        {currentCommunityStmt}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isLocalLoading || isGlobalLoading ? (
                    <div className="max-w-5xl mx-auto px-4 pb-8 text-center text-gray-400 py-12 flex flex-col items-center gap-4">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <p>Loading communities...</p>
                    </div>
                ) : activeTab === 'feed' ? (
                    filteredFeedPosts.length === 0 ? (
                        <div className="max-w-5xl mx-auto px-4 pb-8 text-center py-20 bg-gray-900/30 rounded-3xl border border-white/5">
                            <UsersIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
                            <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
                            <p className="text-gray-500 mb-6">Join some communities or start a conversation in one you belong to!</p>
                            <button onClick={() => setActiveTab('explore')} className="text-blue-400 font-bold hover:underline">Explore</button>
                        </div>
                    ) : (
                        <PostGrid
                            posts={filteredFeedPosts}
                            onPostClick={onPostClick}
                            onLike={onLike}
                            onNavigateToCommunity={onNavigateToCommunity}
                            onBump={onBump}
                            onBookmark={onBookmark}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onNavigateToProfile={onNavigateToProfile}
                            onNavigateToHashtag={onNavigateToHashtag}
                            onReportPost={onReportPost}
                            onVouch={onVouch}
                            animatingLikes={animatingLikes}
                            animatingBumps={animatingBumps}
                            animatingBookmarks={animatingBookmarks}
                            galleryAnimation={galleryAnimation}
                            postBlurOpacity={postBlurOpacity}
                            preferredColumns={gridColumns}
                            onColumnsChange={onGridColumnsChange}
                            preferredScale={preferredScale}
                            onScaleChange={onScaleChange}
                            onSwipeTransition={onSwipeTransition}
                            isDemo={isDemo}
                            isModalOpen={isModalOpen}
                            onShare={onShare}
                            initialVisibleCount={initialVisibleCount}
                            onVisibleCountChange={onVisibleCountChange}
                            isLoading={isGlobalLoading || isLocalLoading}
                            isShakingMode={isShakingMode}
                            postcardGap={postcardGap}
                            onPostcardGapChange={onPostcardGapChange}
                        />
                    )
                ) : (
                    <div className="max-w-5xl 2xl:max-w-[1800px] mx-auto px-4 pb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-6">
                        {displayedCommunities.length === 0 ? (
                            <div className="col-span-1 md:col-span-2 lg:col-span-3 2xl:col-span-6 text-center py-20 bg-gray-900/30 rounded-3xl border border-white/5">
                                <UsersIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                <h3 className="text-xl font-semibold mb-2 text-white" style={stickerTextStyle(0.5)}>No communities to explore</h3>
                                <p className="text-gray-300" style={stickerTextStyle(0.5)}>There are no public communities available right now.</p>
                            </div>
                        ) : displayedCommunities.map((community: Community) => (
                            <button
                                key={community.name}
                                onClick={() => onNavigateToCommunity(community.name)}
                                className="text-left group bg-gray-900/40 p-6 rounded-3xl border border-white/5 hover:border-blue-500/50 transition-all hover:translate-y-[-4px] shadow-xl"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0">
                                        {community.avatar ? (
                                            <img src={community.avatar} alt={community.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/30 transition-colors">
                                                <UsersIcon className="w-6 h-6 text-blue-400" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-raised text-white mb-2 group-hover:text-blue-400 transition-colors" style={stickerTextStyle(0.5)}>{community.name}</h3>
                                <p className="text-white text-sm line-clamp-2 mb-4 h-10" style={stickerTextStyle(0.5)}>{community.bio || 'A growing community of like-minded people.'}</p>
                                <div className="flex items-center gap-4 text-xs text-white border-t border-white/5 pt-4">
                                    <span className="flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {community.members.length}</span>
                                    {community.isPrivate && <span className="flex items-center gap-1">🔒 Private</span>}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onNavigateToCommunity(community.name, 'chat'); }}
                                        className="ml-auto p-2 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded-full transition-colors"
                                        title="Community Chat"
                                    >
                                        <MailIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals Container */}
            {showCreateModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateModal(false)}>
                    <div 
                        className="p-6 md:p-8 rounded-[2.5rem] w-full max-w-md shadow-2xl relative" 
                        style={{ backgroundColor: modalBg, color: textColor }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowCreateModal(false)}
                            className="absolute top-6 right-6 text-gray-500 hover:text-white"
                        >
                            ✕
                        </button>
                        <form onSubmit={handleCreate}>
                            <div className="pb-8 border-b border-white/5 bg-gradient-to-br from-blue-600/10 to-transparent">
                                <h2 className="text-2xl font-bold text-raised mb-1" style={{ color: themeColor, ...stickerTextStyle(0.15) }}>Create Community</h2>
                                <p className="text-sm text-gray-400">Start a new space for your interests and connections.</p>
                            </div>
                            <div className="py-8 space-y-6">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Community Name</label>
                                    <input name="name" type="text" required placeholder="e.g. Photography Lovers" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Description</label>
                                    <textarea name="bio" placeholder="Tell everyone what this group is about..." className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 transition-colors h-32 resize-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Who can post?</label>
                                        <select name="whoCanPost" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:border-blue-500 appearance-none">
                                            <option value="all">Everyone</option>
                                            <option value="admins">Only Admins</option>
                                        </select>
                                    </div>
                                    <div className="flex items-end mb-4">
                                        <label className="flex items-center gap-3 cursor-pointer group">
                                            <div className="relative">
                                                <input name="isPrivate" type="checkbox" className="peer sr-only" />
                                                <div className="h-6 w-11 rounded-full bg-white/10 transition-colors peer-checked:bg-blue-600"></div>
                                                <div className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-all peer-checked:left-6"></div>
                                            </div>
                                            <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">Private Community</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="p-8 bg-black/20 flex gap-4">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 py-4 text-gray-400 font-bold hover:text-white transition-colors">Cancel</button>
                                <button type="submit" className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20">Create Community</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
